import useSWR from 'swr'

import { FetchError } from '../../../data/fetchError'
import { FetchResponse } from '../../../data/fetchResponse'
import { FetchResult } from '../../../data/fetchResult'
import { fetchJson } from '../../../data/fetchers'
import {
  AllowanceCharge,
  Invoice,
  InvoiceItem,
  TermOfPayment,
  WebEdiDto,
} from '../../../data/models'
import { useConfigContext } from '../../ui-config'

// Warning pulling (refreshInterval) causes high costs in Azure!
// This was activated to show the user current data to test it
// but this needs to be replaced with optimistic UI with SignalR in the future,
// see ticket AN05873-230.

// get all invoices
export function useAllInvoices(): FetchResult<WebEdiDto<Invoice>[]> {
  const { pollingConfig } = useConfigContext()
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<WebEdiDto<Invoice>[]>,
    FetchError
  >(`/Invoices`, fetchJson<WebEdiDto<Invoice>[]>, {
    refreshInterval: pollingConfig.intervalInSeconds * 1000,
  })

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get invoice
export function useInvoiceById(
  invoiceId: string,
): FetchResult<WebEdiDto<Invoice>> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<WebEdiDto<Invoice>>,
    FetchError
  >(`/Invoices/${invoiceId}`, fetchJson<WebEdiDto<Invoice>>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get all allowance charges
export function useAllInvoiceAllowanceCharges(
  invoiceId: string,
): FetchResult<AllowanceCharge[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<AllowanceCharge[]>,
    FetchError
  >(`/Invoices/${invoiceId}/AllowanceCharges`, fetchJson<AllowanceCharge[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get allowance charge
export function useInvoiceAllowanceChargeById(
  invoiceId: string,
  id: string,
): FetchResult<AllowanceCharge> {
  const { data, isLoading, error, mutate } = useSWR<
    FetchResponse<AllowanceCharge>,
    FetchError
  >(`/Invoices/${invoiceId}/AllowanceCharges/${id}`, fetchJson<AllowanceCharge>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get all terms of payments
export function useAllInvoiceTermsOfPayments(
  invoiceId: string,
): FetchResult<TermOfPayment[]> {
  const { data, isLoading, error, mutate } = useSWR<
    FetchResponse<TermOfPayment[]>,
    FetchError
  >(`/Invoices/${invoiceId}/TermsOfPayments`, fetchJson<TermOfPayment[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get term of payment
export function useInvoiceTermOfPaymentById(
  invoiceId: string,
  id: string,
): FetchResult<TermOfPayment> {
  const { data, isLoading, error, mutate } = useSWR<
    FetchResponse<TermOfPayment>,
    FetchError
  >(`/Invoices/${invoiceId}/TermsOfPayments/${id}`, fetchJson<TermOfPayment>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get all invoice items
export function useAllInvoiceItems(
  invoiceId: string,
): FetchResult<InvoiceItem[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<InvoiceItem[]>,
    FetchError
  >(`/Invoices/${invoiceId}/Items`, fetchJson<InvoiceItem[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get invoice item
export function useInvoiceItemById(
  invoiceId: string,
  itemId: string,
): FetchResult<InvoiceItem> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<InvoiceItem>,
    FetchError
  >(`/Invoices/${invoiceId}/Items/${itemId}`, fetchJson<InvoiceItem>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get all item allowance charges
export function useAllInvoiceItemAllowanceCharges(
  invoiceId: string,
  itemId: string,
): FetchResult<AllowanceCharge[]> {
  const { data, isLoading, error, mutate } = useSWR<
    FetchResponse<AllowanceCharge[]>,
    FetchError
  >(
    `/Invoices/${invoiceId}/Items/${itemId}/allowancecharges`,
    fetchJson<AllowanceCharge[]>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// get item allowance charge
export function useInvoiceItemAllowanceChargeById(
  invoiceId: string,
  itemId: string,
  id: string,
): FetchResult<AllowanceCharge> {
  const { data, isLoading, error, mutate } = useSWR<
    FetchResponse<AllowanceCharge>,
    FetchError
  >(
    `/Invoices/${invoiceId}/Items/${itemId}/allowancecharges/${id}`,
    fetchJson<AllowanceCharge>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}
